/**
 * 通用截图上传：粘贴(Ctrl+V) / 拖拽 / 选文件 → /attachments/upload → 回传裸 OSS url。
 * 抽自 PaymentRecordModal 的上传逻辑，供加微信「通过截图/头像截图」等截图位复用。
 * 注意：同一弹窗有多个上传框时，只能给「一个」开 enablePaste（否则一次粘贴会同时灌进多个框，串台）。
 */
import { useLayoutEffect, useState } from 'react';
import { message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Props {
  value?: string | null;
  onChange: (url: string) => void;
  entityType: string;
  entityId: number | string;
  fieldName: string;
  /** 仅在弹窗打开且该框是主截图时传 true：挂全局粘贴监听 */
  enablePaste?: boolean;
  maxHeight?: number;
}

export function ScreenshotUpload({ value, onChange, entityType, entityId, fieldName, enablePaste = false, maxHeight = 180 }: Props) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File | Blob) => {
      const fObj = file instanceof File ? file : new File([file], `${fieldName}-${Date.now()}.png`, { type: file.type || 'image/png' });
      if (fObj.size > 50 * 1024 * 1024) throw new Error('图片超 50MB'); // 对齐后端 50MB 上限——原前端 8MB 致超限直接 throw 走 onError、截图框卡"上传中"、给"传了但按钮灰"错觉(L0→L1)
      const fd = new FormData();
      fd.append('file', fObj);
      fd.append('entityType', entityType);
      fd.append('entityId', String(entityId));
      fd.append('fieldName', fieldName);
      const r = await api.post('/attachments/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data as { url?: string; ossUrl?: string; fileUrl?: string };
    },
    // /attachments/upload 返回 AttachmentEntity，URL 字段是 ossUrl（不是 url）——必须读 ossUrl，否则回填 undefined、截图永远存不上
    onSuccess: (r) => {
      const url = r?.url ?? r?.ossUrl ?? r?.fileUrl;
      if (!url) { message.error('上传成功但未拿到图片地址'); return; }
      onChange(url);
      message.success('截图已上传');
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '上传失败'),
  });

  const handleImageFile = (f: File | Blob) => {
    if (previewUrl?.startsWith('blob:')) { try { URL.revokeObjectURL(previewUrl); } catch { /* ignore */ } }
    setPreviewUrl(URL.createObjectURL(f instanceof File ? f : new File([f], 'paste.png', { type: f.type || 'image/png' })));
    upload.mutate(f);
  };

  useLayoutEffect(() => {
    if (!enablePaste) return;
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (blob) { handleImageFile(blob); e.preventDefault(); break; }
        }
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePaste]);

  const shownImg = previewUrl || (value && /^https?:\/\//.test(value) ? value : null);
  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith('image/')) handleImageFile(f); }}
      style={{ border: '2px dashed var(--border, #DEE0E3)', borderRadius: 8, padding: shownImg ? 12 : 24, textAlign: 'center', background: '#fafbfc' }}>
      {shownImg ? (
        <>
          <img src={shownImg} alt="预览" style={{ maxHeight, maxWidth: '100%', borderRadius: 4 }} />
          <div style={{ marginTop: 8, fontSize: 12, color: upload.isPending ? 'var(--primary, #2C5BFF)' : 'var(--success-text, #0FA968)' }}>
            {upload.isPending ? '⬆️ 上传中…' : '✓ 已上传'}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 28 }}>📷</div>
          <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', margin: '8px 0' }}>
            {enablePaste ? '拖拽 / Ctrl+V 粘贴 / 选文件' : '拖拽 / 选文件'}
          </div>
          <label style={{ color: 'var(--primary, #2C5BFF)', cursor: 'pointer' }}>
            选择本地文件
            <input type="file" accept="image/*" style={{ display: 'none' }}
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f); }} />
          </label>
        </>
      )}
    </div>
  );
}
